import { useEffect, useRef } from 'react';
import { api } from '../../../utils/api';

// Measures how long the current operator spends assessing each quote and posts
// it to the backend, which keeps a cumulative (deal_id, user_id) total. No UI —
// this is data only (time per quote, and thus per item). See the backend route
// POST /api/deals/:id/assessment-time and migration 0095.
//
// The clock runs only while the tab is visible and in use (input within the
// last 3 minutes). We accrue whole-second DELTAS
// and flush them: every FLUSH_INTERVAL_MS, when the tab is hidden, when the
// quote changes, and on pagehide. Because each flush is an increment on the
// server, leaving and reopening the page continues the count, and no unload
// event has to fire for time to be saved — a missed one loses only the last
// unflushed tail.

const FLUSH_INTERVAL_MS = 15000;
// A single visible segment cannot legitimately exceed this. A larger gap means
// the timer was suspended (laptop sleep, throttled background timer, clock
// jump), not that the operator worked, so we clamp it out.
const MAX_SEGMENT_MS = 2 * FLUSH_INTERVAL_MS;
// 'Visible' is not 'in use': Firefox and Chrome on Linux report a window covered
// by another app as visible, so a tab left behind a terminal counted for hours
// (2026-10-14). The clock also pauses after this long with no input on the page,
// and resumes on the next input. Three minutes leaves room to study photos.
const IDLE_MS = 3 * 60 * 1000;
const INPUT_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'];

// Move the running segment into the pending total, guarding against a suspended
// clock. The caller decides whether to start a fresh segment afterwards.
function foldSegment(s, now) {
    if (s.segmentStart == null) return;
    const elapsed = Math.min(Math.max(0, now - s.segmentStart), MAX_SEGMENT_MS);
    s.pendingMs += elapsed;
    s.segmentStart = null;
}

// Post the whole seconds accrued so far as an increment against `dealId`,
// keeping the sub-second remainder for the next flush. Fire-and-forget: a
// dropped heartbeat is recovered by the next one.
function flush(s, dealId, keepalive) {
    const seconds = Math.floor(s.pendingMs / 1000);
    if (!dealId || seconds < 1) return;
    s.pendingMs -= seconds * 1000;
    const body = { seconds, reference_code: s.referenceCode ?? null, item_count: s.itemCount ?? null };
    api.post(`/api/deals/${dealId}/assessment-time`, body, keepalive ? { keepalive: true } : {}).catch(() => {});
}

export function useAssessmentTimer(dealId, referenceCode, itemCount) {
    // All timer state lives in a ref so ordinary re-renders (queue updates, item
    // edits) never reset the running clock.
    const state = useRef({ dealId: null, referenceCode: null, itemCount: null, pendingMs: 0, segmentStart: null, lastInputAt: Date.now() });

    // Interval + visibility + pagehide + input are wired once. They read the
    // current quote and metadata from the ref, which the effect below keeps fresh.
    useEffect(() => {
        const s = state.current;

        const tick = () => {
            if (s.segmentStart == null) return;   // paused (hidden, idle or no quote)
            const now = Date.now();
            const idleFrom = s.lastInputAt + IDLE_MS;
            if (now > idleFrom) {
                // Credit up to the idle cut-off, then pause until the next input.
                foldSegment(s, Math.max(s.segmentStart, idleFrom));
                flush(s, s.dealId, false);
                return;
            }
            foldSegment(s, now);
            s.segmentStart = now;                 // keep counting
            flush(s, s.dealId, false);
        };
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                foldSegment(s, Date.now());
                flush(s, s.dealId, false);        // leave paused (segmentStart null)
            } else if (s.dealId) {
                s.lastInputAt = Date.now();       // coming back to the tab is activity
                s.segmentStart = Date.now();       // resume
            }
        };
        const onInput = () => {
            const now = Date.now();
            s.lastInputAt = now;
            if (s.segmentStart == null && s.dealId && document.visibilityState !== 'hidden') {
                s.segmentStart = now;              // resume after an idle pause
            }
        };
        const onPageHide = () => {
            foldSegment(s, Date.now());
            flush(s, s.dealId, true);
        };

        const interval = setInterval(tick, FLUSH_INTERVAL_MS);
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', onPageHide);
        INPUT_EVENTS.forEach(e => window.addEventListener(e, onInput, { passive: true }));
        // Scroll does not bubble, so catch it on the way down from any scroller.
        document.addEventListener('scroll', onInput, { passive: true, capture: true });
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', onPageHide);
            INPUT_EVENTS.forEach(e => window.removeEventListener(e, onInput));
            document.removeEventListener('scroll', onInput, { capture: true });
        };
    }, []);

    // Track the current quote. Re-runs when the quote changes (flush the one we
    // are leaving first) or when its reference/item count changes (so the flushed
    // total carries the latest metadata). Only this effect writes the ref, so its
    // cleanup always sees the values of the quote it is leaving.
    useEffect(() => {
        const s = state.current;
        s.dealId = dealId ?? null;
        s.referenceCode = referenceCode ?? null;
        s.itemCount = Number.isFinite(itemCount) ? itemCount : null;
        s.pendingMs = 0;
        s.lastInputAt = Date.now();               // opening a quote is activity
        s.segmentStart = (s.dealId && document.visibilityState !== 'hidden') ? Date.now() : null;

        return () => {
            foldSegment(s, Date.now());
            flush(s, s.dealId, false);
            s.pendingMs = 0;
            s.segmentStart = null;
        };
    }, [dealId, referenceCode, itemCount]);
}
